import { useCallback } from "react";
import { useAppDispatch } from "../hooks";
import { uploadImageAction, clearUrl } from "./ImageActions";

/**
 * Shared single-image upload primitive (SCRUM-900 / SCRUM-911).
 *
 * `uploadImageAction` already resolves to the uploaded (secure) URL, so a form can
 * upload-then-save as a plain awaited sequence instead of the legacy
 * `state.images.url` watch-effect + `loading`-flag + `clearUrl` dance:
 *
 *   const url = await uploadImage(image.data, path);
 *   await save({ ...entity, image: url });
 *
 * Deliberate scope / contract (SCRUM-911):
 * - Returns the URL. The caller owns the save, the success/error toast, the
 *   loading flag and any navigation — in nearly every consumer those are wired to
 *   separate redux slice message/error effects, so this primitive stays out of
 *   them.
 * - Clears the shared `images.url` right after a successful upload so no stale
 *   value re-triggers other url-watching effects — notably the media browsers
 *   (IndexMedia / IndexModelMedia, SCRUM-904). It does NOT otherwise own
 *   `clearUrl`, and it deliberately does not centralise `clearUrl` for those
 *   media consumers (they drive their own upload + gallery-cache flow).
 * - Errors propagate: `uploadImageAction` re-throws on a failed upload
 *   (SCRUM-815), so the caller's try/catch surfaces the failure and skips the
 *   save (no `clearUrl` runs on a failed upload).
 *
 * Returns `uploadImage`.
 */
export function useImageUpload(): (data: string, path: string) => Promise<string> {
  const dispatch = useAppDispatch();
  return useCallback(
    async (data: string, path: string) => {
      const url = await dispatch(uploadImageAction(data, path));
      dispatch(clearUrl());
      return url;
    },
    [dispatch]
  );
}

